import React, { useCallback, useEffect, useMemo, useState } from 'react';
import sortBy from 'lodash/sortBy';
import Divider from 'components/Divider';
import RadioButton from 'components/RadioButton';
import { GraniteSelect } from 'components/Select/Select';
import { GraniteLabel } from 'components/V2/Label/GraniteLabel';
import { ProductType as AccessProductType } from 'screens/SelectServices/schemas';

import { GraniteButton } from 'components/V2/Button/GraniteButton';
import { ReactComponent as Pin } from 'assets/images/pin.svg';
import clsx from 'clsx';
import {
  ProductOfferingType,
  ProductType,
} from 'screens/SelectServices/schemas';
import {
  TermType,
  buildNestedObjectWithIds,
  formatBandwidth,
  formatCarrier,
  formatTerm,
  getServiceActivationFee,
  groupByMultipleKeys,
  parseBandwidth,
} from 'screens/SelectServices/utils';
import IconButton from 'components/IconButton';
import { motion } from 'framer-motion';
import { AccessServiceSummary } from './AccessServiceSummary';
import ConstructionBanner from './ConstructionBanner/ConstructionBanner';
import { formatCurrency } from 'shared/util/formatCurrency';
import {
  MNS_Package,
  SelectedPackages,
  useSingleServiceManager,
} from 'context/SingleServiceManagerContext';
import { useCartContext } from '../CartProvider';
import { observer } from 'mobx-react-lite';
import { AddToCartButton } from './AddToCartButton';
import { CONSTRUCTION_LABEL, getMNSPrice } from '../utils';
import Checkbox from 'components/Checkbox';

const keys = [
  'provider',
  'term',
  'bandwidth_download',
  'bandwidth_upload',
  'ip_type',
  'ip_block',
];

const SEPARATOR = '|||';

interface OfferSettingsProps {
  data: {
    address: string;
    id: string;
    offerings: ProductOfferingType[];
    type?: string;
    initialMNS?: SelectedPackages | null;
  };
  isPinnedSummary?: boolean;
  classname?: string;
}

interface SelectOptions {
  [key: string]: Array<{ label: string; value: string }> | undefined;
}

export const OfferSettings: React.FC<OfferSettingsProps> = observer(
  ({ data, isPinnedSummary }) => {
    const {
      pinService,
      unpinService,
      handleProductChange: onChange,
      selectedValuesMap,
      updateSelectedValues,
      setProductMap,
      pinnedServices,
    } = useSingleServiceManager();

    const pinnedServicesMNS = useMemo(() => {
      const key = `${data.id}_${data.type}`;
      return pinnedServices[key]?.initialMNS;
    }, [data.id, data.type, pinnedServices]);

    const {
      addAccessCartItem,
      isItemAlreadyInCart,
      removeAccessCartItem,
      hasCircuitLimitReached,
    } = useCartContext();

    const selectedValues = useMemo(() => {
      const compositeKey = `${data.id}_${data.type}_${!!isPinnedSummary}`;
      return selectedValuesMap[compositeKey] || {};
    }, [data.id, data.type, isPinnedSummary, selectedValuesMap]);

    const normData = data?.offerings
      ?.map((d) => ({
        ...d,
        ip_block: d.ip_block ?? 'N/A',
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        ip_type: d?.ip_type ?? 'STATIC',
      }))
      .map((d) => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        if (d.bandwidth) {
          return {
            ...d,
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-expect-error
            bandwidth_download: d.bandwidth,
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-expect-error
            bandwidth_upload: d.bandwidth,
          };
        }
        return d;
      });

    const sorted = sortBy(normData, [
      (item) => (item.term === '3 YR' ? 0 : 1),
      (item) => -item.priority || 0,
    ]);

    const grouped = groupByMultipleKeys(sorted, keys);

    const keysOptionsList = Object.entries(grouped).map(([key, values]) => {
      return { key, id: values[0].id };
    });

    const nestedList = buildNestedObjectWithIds(keysOptionsList);

    const [options, setOptions] = useState<SelectOptions>({
      provider: [],
      term: [],
      bandwidth_download: [],
      bandwidth_upload: [],
      ip_block: [],
    });

    useEffect(() => {
      // Check if there are already selected values for this service
      const compositeKey = `${data.id}_${data.type}_${!!isPinnedSummary}`;
      const existingSelectedValues = selectedValuesMap[compositeKey];

      if (existingSelectedValues) {
        // If there are existing selected values, use them to preselect options
        preselectOptions('provider', existingSelectedValues, false);
      } else {
        // Otherwise, initialize with default values
        preselectOptions('provider', {}, !isPinnedSummary);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data.id, data.type, isPinnedSummary]);

    const preselectOptions = (
      key: string,
      newSelectedValues: Record<string, string>,
      shouldRunUpdate: boolean = true,
    ) => {
      const start = keys.indexOf(key);
      const newList: Record<string, string> = {};

      const selectedOptions: string[] = [];

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let next: any = nestedList;

      for (let i = 0; i < keys.indexOf(key); i++) {
        selectedOptions.push(newSelectedValues[keys[i]]);
        next = next[newSelectedValues[keys[i]]];
      }

      const newOptions: Record<
        string,
        Array<{ label: string; value: string }>
      > = {};

      for (let i = start; i < keys.length; i++) {
        newOptions[keys[i]] = Object.keys(next).map((opt) => ({
          value: opt,
          label: opt ?? 'N/A',
        }));

        newList[keys[i]] = Object.keys(next)[0];

        next = next[newList[keys[i]]] ?? {};
      }

      setOptions((prevOptions) => ({
        ...prevOptions,
        ...newOptions,
      }));

      if (shouldRunUpdate) {
        updateSelectedValues(data.id, data.type!, !!isPinnedSummary, newList);
      }
    };

    const handleSelectChange = (
      key: string,
      value: string,
      // autoSelectNext = false,
    ) => {
      const newSelectedValues = {
        ...selectedValues,
        [key]: value,
      };
      const nextIndex = keys.indexOf(key) + 1;

      if (nextIndex < keys.length) {
        preselectOptions(keys[nextIndex], newSelectedValues, true);
      }

      updateSelectedValues(data.id, data.type!, !!isPinnedSummary, {
        [key]: value,
      });
    };

    const [mns, setMns] = useState<SelectedPackages>(
      pinnedServicesMNS ??
        (data.type === 'broadband'
          ? { essential: true, enhanced: false }
          : { essential: false, enhanced: true }),
    );

    useEffect(() => {
      if (pinnedServicesMNS) {
        setMns(pinnedServicesMNS);
      }
    }, [pinnedServicesMNS]);

    const handleSelectMNSChange = useCallback((pkg: MNS_Package) => {
      setMns((prev) => {
        const isSelected = prev[pkg];

        const newSelected = Object.fromEntries(
          Object.keys(prev).map((key) => [key, false]),
        ) as SelectedPackages;

        if (!isSelected) {
          newSelected[pkg] = true;
        }

        return newSelected;
      });
    }, []);

    const summaryData = useMemo(() => {
      const key = keys
        .map((key) => selectedValues[key] || 'N/A')
        .join(SEPARATOR);
      const product = grouped[key]?.[0] as unknown as ProductType;
      const term = grouped[key]?.[0].term as TermType;
      const construction_likely = grouped[key]?.[0].construction_likely;
      onChange(data.id, data.type!, isPinnedSummary!, product, null);
      return {
        ...product,
        term,
        construction_likely,
        ...mns,
        type: product?.formal_broadband_pricing ? 'broadband' : 'dia',
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedValues]);

    const accessTypeOptions = [
      { value: 'fe', label: 'FE' },
      {
        value: summaryData?.type === 'broadband' ? 'cable' : 'ge',
        label: summaryData?.type === 'broadband' ? 'Cable' : 'GE',
      },
    ];
    const handlePin = useCallback(() => {
      const defaultServiceKey = `${data.id}_${data.type}_false`;
      updateSelectedValues(
        data.id,
        data.type!,
        true,
        selectedValuesMap[defaultServiceKey] || {},
      );
      pinService(data.id, data.type!, {
        ...data,
        initialMNS: mns,
      });
    }, [data, mns, pinService, selectedValuesMap, updateSelectedValues]);

    const handleUnpin = useCallback(() => {
      unpinService(data?.id, data?.type ?? '');
      setProductMap((current) => {
        return {
          ...current,
        };
      });
    }, [data.id, data.type, setProductMap, unpinService]);

    const handleAddToCart = useCallback(async () => {
      const key = keys
        .map((key) => selectedValues[key] || 'N/A')
        .join(SEPARATOR);
      const product = grouped[key]?.[0] as unknown as AccessProductType;
      const term = grouped[key]?.[0].term as TermType;
      const construction_likely = grouped[key]?.[0].construction_likely;

      const offeringID = product.id;

      if (isItemAlreadyInCart(offeringID)) {
        await removeAccessCartItem(offeringID);
      } else {
        await addAccessCartItem({
          ...product,
          term,
          construction_likely,
          isOtherOption: true,
          mns_essential: mns.essential,
          mns_enhanced: mns.enhanced,
        });
      }
    }, [
      addAccessCartItem,
      grouped,
      isItemAlreadyInCart,
      removeAccessCartItem,
      selectedValues,
      mns,
    ]);

    const { isAddedToCart, isDisabled } = useMemo(() => {
      const key = keys
        .map((key) => selectedValues[key] || 'N/A')
        .join(SEPARATOR);
      const product = grouped[key]?.[0] as unknown as ProductType;
      return {
        isAddedToCart: isItemAlreadyInCart(product?.id),
        isDisabled: hasCircuitLimitReached('Broadband', product?.id),
      };
    }, [grouped, hasCircuitLimitReached, isItemAlreadyInCart, selectedValues]);

    const [isExpanded, setIsExpanded] = useState(false);

    const pricing =
      summaryData?.formal_broadband_pricing || summaryData?.formal_dia_pricing;

    const serviceActivationFee = getServiceActivationFee(summaryData);

    const responsiveLabelShrink =
      '[&>span:first-of-type]:max-sm:text-xs [&>span:first-of-type]:md:text-[13.5px] [&>span:first-of-type]:xl:text-base';

    const totalMRC = useMemo(() => {
      const key = keys
        .map((key) => selectedValues[key] || 'N/A')
        .join(SEPARATOR);
      const productOffering = grouped[key]?.[0] as unknown as AccessProductType;
      return (
        (pricing?.total_mrc ?? 0) +
        getMNSPrice({
          ...productOffering,
          mns_enhanced: mns.enhanced,
          mns_essential: mns.essential,
        })
      );
    }, [
      grouped,
      mns.enhanced,
      mns.essential,
      pricing?.total_mrc,
      selectedValues,
    ]);

    const fieldBackgroundClassName = useMemo(
      () =>
        isAddedToCart
          ? 'select-background-base-surface-1-pinned'
          : 'select-background-base-surface-1',
      [isAddedToCart],
    );

    return (
      <div
        className={clsx(
          'relative flex h-full max-h-full w-full flex-col gap-4 p-6 sm:w-1/2',
          isAddedToCart && 'added-to-cart-service',
          isPinnedSummary && '!w-full',
        )}
      >
        <div className="flex flex-col justify-between gap-6 lg:flex-row xl:flex-wrap">
          <p className="flex max-w-max flex-col justify-center font-bold">
            <span
              className="group flex h-[38px] cursor-pointer items-center gap-1"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              <span className="text-[28px] text-content-base-default">
                {formatCurrency(totalMRC)}
              </span>
              <span className="self-center text-xl text-content-base-subdued">
                /month
              </span>
              <IconButton
                icon="chevronDown"
                className={clsx(
                  isExpanded && 'rotate-180',
                  'group-hover:text-content-accent-default',
                )}
                buttonType="ghost"
                color="currentColor"
                type="button"
              />
            </span>
            <span className="text-xs">
              +${serviceActivationFee} service activation fee
            </span>
          </p>
          <div className="flex w-full max-w-[190px] flex-col gap-2 lg:self-end">
            <AddToCartButton
              isSelected={isAddedToCart}
              onAddToCart={handleAddToCart}
              disabled={isDisabled}
            />
            <GraniteButton
              variant="secondary"
              size="medium"
              className="hidden md:flex"
              onClick={(e) => {
                e.stopPropagation();
                isPinnedSummary ? handleUnpin() : handlePin();
              }}
            >
              {!isPinnedSummary ? 'Pin to compare' : 'Unpin'}
              <Pin color="inherit" />
            </GraniteButton>
          </div>
        </div>
        <motion.div
          key="other-options"
          initial={{ opacity: 0, height: 0 }}
          animate={{
            opacity: isExpanded ? 1 : 0,
          }}
          transition={{ duration: 0.4, ease: 'easeInOut' }}
          className={clsx(
            'pointer-events-none flex w-full flex-col gap-4',
            isExpanded && 'pointer-events-auto relative z-10',
          )}
        >
          <AccessServiceSummary
            data={summaryData}
            isPinnedSummary={isPinnedSummary}
            mns={mns}
          />
        </motion.div>
        <motion.div
          initial={{ y: 0, height: 0 }}
          animate={{ y: isExpanded ? 270 : 0, height: 'auto' }}
          transition={{ duration: 0.4, ease: 'easeInOut' }}
          className="w-full"
        >
          <Divider />
        </motion.div>
        {!isExpanded && summaryData.construction_likely && (
          <ConstructionBanner text={CONSTRUCTION_LABEL} />
        )}
        <motion.div
          initial={{ opacity: 1 }}
          animate={{
            opacity: isExpanded ? 0 : 1,
          }}
          transition={{ duration: 0.4, ease: 'easeInOut' }}
          className={clsx('w-full', isExpanded && 'pointer-events-none z-0')}
        >
          <div className="grid grid-cols-2 gap-6">
            <GraniteLabel
              className={clsx(
                responsiveLabelShrink,
                summaryData?.type === 'broadband' && 'hidden',
              )}
              label="Connection type"
              element="div"
            >
              <GraniteSelect
                key={isPinnedSummary ? 'pinned' : 'unpinned'}
                className={clsx('w-full', fieldBackgroundClassName)}
                value={accessTypeOptions[1]}
                options={accessTypeOptions}
                isDisabled
                variant="blue"
              />
            </GraniteLabel>
            <GraniteLabel
              label="Carrier name"
              element="div"
              className={responsiveLabelShrink}
            >
              <GraniteSelect
                className={clsx('w-full', fieldBackgroundClassName)}
                onChange={(selectedOption) =>
                  handleSelectChange('provider', selectedOption?.value || '')
                }
                value={
                  selectedValues.provider
                    ? {
                        value: selectedValues.provider,
                        label: formatCarrier(selectedValues.provider),
                      }
                    : null
                }
                name="provider"
                options={options.provider?.map((option) => ({
                  ...option,
                  label: formatCarrier(option.label),
                }))}
                variant="blue"
              />
            </GraniteLabel>
            <GraniteLabel
              label="Term length"
              element="div"
              className={responsiveLabelShrink}
            >
              <GraniteSelect
                className={clsx('w-full', fieldBackgroundClassName)}
                onChange={(selectedOption) =>
                  handleSelectChange('term', selectedOption?.value || '')
                }
                value={
                  selectedValues.term
                    ? {
                        value: selectedValues.term,
                        label: formatTerm(selectedValues.term),
                      }
                    : null
                }
                name="term"
                options={sortBy(
                  options.term?.map((option) => ({
                    value: option.value,
                    label: formatTerm(option.label),
                  })),
                  (option) => option.label.toLowerCase(),
                )}
                isDisabled={!selectedValues.provider}
                variant="blue"
              />
            </GraniteLabel>
            <GraniteLabel
              label="Download"
              element="div"
              className={responsiveLabelShrink}
            >
              <GraniteSelect
                className={clsx(
                  'w-full overflow-visible',
                  fieldBackgroundClassName,
                )}
                onChange={(selectedOption) =>
                  handleSelectChange(
                    'bandwidth_download',
                    selectedOption?.value || '',
                  )
                }
                value={
                  selectedValues.bandwidth_download
                    ? {
                        value: selectedValues.bandwidth_download,
                        label: formatBandwidth(
                          selectedValues.bandwidth_download,
                        ),
                      }
                    : null
                }
                name="bandwidth_download"
                options={options.bandwidth_download
                  ?.sort(
                    (a, b) => parseBandwidth(a.value) - parseBandwidth(b.value),
                  )
                  .map((option) => ({
                    value: option.value,
                    label: formatBandwidth(option.label),
                  }))}
                isDisabled={!selectedValues.term}
                variant="blue"
              />
            </GraniteLabel>

            <GraniteLabel
              label="Upload"
              element="div"
              className={responsiveLabelShrink}
            >
              <GraniteSelect
                className={clsx('w-full', fieldBackgroundClassName)}
                onChange={(selectedOption) =>
                  handleSelectChange(
                    'bandwidth_upload',
                    selectedOption?.value || '',
                  )
                }
                value={
                  selectedValues.bandwidth_upload
                    ? {
                        value: selectedValues.bandwidth_upload,
                        label: formatBandwidth(selectedValues.bandwidth_upload),
                      }
                    : null
                }
                name="bandwidth_upload"
                options={options.bandwidth_upload
                  ?.sort(
                    (a, b) => parseBandwidth(a.value) - parseBandwidth(b.value),
                  )
                  .map((option) => ({
                    value: option.value,
                    label: formatBandwidth(option.label),
                  }))}
                variant="blue"
              />
            </GraniteLabel>
            <GraniteLabel
              label="IP type"
              element="div"
              className={responsiveLabelShrink}
            >
              <RadioButton
                disabled={!options.ip_type?.some((i) => i.value === 'DYNAMIC')}
                options={[{ value: 'DYNAMIC', label: 'Dynamic IP' }]}
                selectedValue={selectedValues.ip_type}
                onChange={() => handleSelectChange('ip_type', 'DYNAMIC')}
                className="mb-2"
                variant="blue"
              />
              <RadioButton
                disabled={!options.ip_type?.some((i) => i.value === 'STATIC')}
                options={[{ value: 'STATIC', label: 'Static IP' }]}
                selectedValue={selectedValues.ip_type}
                onChange={() => handleSelectChange('ip_type', 'STATIC')}
                variant="blue"
              />
            </GraniteLabel>
            <GraniteLabel
              label="IP block"
              element="div"
              className={responsiveLabelShrink}
            >
              <GraniteSelect
                className={clsx('w-full', fieldBackgroundClassName)}
                onChange={(selectedOption) =>
                  handleSelectChange('ip_block', selectedOption!.value)
                }
                value={
                  selectedValues.ip_block
                    ? {
                        value: selectedValues.ip_block,
                        label: selectedValues.ip_block,
                      }
                    : null
                }
                name="ip_block"
                options={options.ip_block?.map((i) => ({
                  ...i,
                  label: i.label,
                }))}
                variant="blue"
                isDisabled={selectedValues.ip_type === 'DYNAMIC'}
              />
            </GraniteLabel>
            <GraniteLabel
              label="Add network monitoring"
              element="div"
              className={clsx(responsiveLabelShrink, 'col-span-2')}
            >
              <div className="flex flex-row gap-6">
                <Checkbox
                  variant="blue"
                  label="MNS Essentials"
                  checked={mns.essential === true}
                  onChange={() => handleSelectMNSChange('essential')}
                  className={summaryData.type !== 'broadband' ? 'hidden' : ''}
                />

                <Checkbox
                  variant="blue"
                  label="MNS Enhanced"
                  checked={mns.enhanced === true}
                  onChange={() => handleSelectMNSChange('enhanced')}
                />
              </div>
            </GraniteLabel>
          </div>
        </motion.div>
      </div>
    );
  },
);
